import React, { useState, useEffect, useMemo } from 'react';
import FormDrawer from '../form-drawer';
import { ButtonLoader, Input } from "components/__new/common/form";
import greenbar from "assets/images/icons/green-rectangle-bar.svg";
import whitebar from "assets/images/icons/white-rectangle-bar.svg";
import SlideToggle from "components/__new/common/form/SlideToggle";

import { useDispatch, useSelector } from 'react-redux'
import { toast } from "react-toastify";

import * as settingsActions from "store/entities/settingsInfo/action";

import validate from "utils/validate";
import ModalAlert from 'components/__new/common/ModalAlert';
import Textarea from './Textarea';
import { REGEX_VALUES } from 'utils/regexValues';


const EditRoleModal = (props) => {
    const { isOpen, setIsOpen, data: permissionsData, roles, getAllRoles } = props;
    const dispatch = useDispatch();
    const { editRoleLoading, editRole } = useSelector(state => state.entities.settingsInfo);

    const [toggleActive, setToggleActive] = useState(false);
    const [isCreated, setIsCreated] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [isOpenModalAlertSuccess, setIsOpenModalAlertSuccess] = useState(false);
    const [selectedPermissions, setSelectedPermissions] = useState(new Set());

    const [formErrors, setFormErrors] = useState({
        name: null,
        description: null,
    });
    const [form, setForm] = useState({
        name: roles?.name || "",
        description: roles?.description || ""
    });
    const handleToggle = () => {
        setToggleActive(!toggleActive);
        setSelectedPermissions(prevSelected => {
            const newSelected = new Set(prevSelected);
            if (!toggleActive) {
                permissionsData?.data?.forEach(permission => {
                    newSelected.add(permission.parentPermission.id);
                    permission.companiesRolePermission.forEach(cp => newSelected.add(cp.id));
                });
            } else {
                permissionsData?.data?.forEach(permission => {
                    newSelected.delete(permission.parentPermission.id);
                    permission.companiesRolePermission.forEach(cp => newSelected.delete(cp.id));
                });
            }
            return newSelected;
        });
    };

    const initial = () => {
        const initialSelected = new Set();
        permissionsData?.data?.forEach(permission => {
            // Check if all children have hasPermission set to true
            const allChildrenHavePermission = permission.companiesRolePermission.every(companyPermission => companyPermission.hasPermission);
            if (allChildrenHavePermission) {
                initialSelected.add(permission.parentPermission.id);
            }
            permission.companiesRolePermission.forEach(companyPermission => {
                if (companyPermission.hasPermission) {
                    initialSelected.add(companyPermission.id);
                }
            });
        });
        return initialSelected;
    };

    const handleCheckboxChange = (id, isParent = false, childPermissions = []) => {
        setSelectedPermissions((prevSelected) => {
            const newSelected = new Set(prevSelected);

            if (newSelected.has(id)) {
                newSelected.delete(id);
                if (isParent) {
                    childPermissions.forEach(childId => newSelected.delete(childId));
                }
            } else {
                newSelected.add(id);
                if (isParent) {
                    childPermissions.forEach(childId => newSelected.add(childId));
                }
            }

            if (!isParent) {
                const parentPermission = permissionsData?.data?.find(permission =>
                    permission.companiesRolePermission.some(companyPermission => companyPermission.id === id)
                );
                if (parentPermission) {
                    const allChildrenSelected = parentPermission.companiesRolePermission.every(companyPermission =>
                        newSelected.has(companyPermission.id)
                    );
                    if (allChildrenSelected) {
                        newSelected.add(parentPermission.parentPermission.id);
                    } else {
                        newSelected.delete(parentPermission.parentPermission.id);
                    }
                }
            }

            // Check toggle state based on all children selection
            let allChildrenSelected = true;
            permissionsData?.data?.forEach(permission => {
                permission.companiesRolePermission.forEach(cp => {
                    if (!newSelected.has(cp.id)) {
                        allChildrenSelected = false;
                    }
                });
            });

            setToggleActive(allChildrenSelected);
            return newSelected;
        });
    };
    useEffect(() => {
        setSelectedPermissions(initial());
    }, [permissionsData]);

    
const handleNameChange = (e) => {
    const { name, value } = e.target;
      if ((name === "role" ) && (value.length < 2)) {
        setFormErrors({
          ...formErrors,
          name: "Role name must be at least 2 characters long",
      })
      } else {
        setFormErrors({
          ...formErrors,
          name: null,
      })
      }
      if (REGEX_VALUES.LETTERS_WITH_UNDERSCORE_AMPERSAND_AND_HYPHEN_ONLY.test(value)) {
        setForm({...form, name: value})
      } 
}

const handleDescriptionChange = (e) => {
    const { name, value } = e.target;
      if ((name === "description" ) && (value.length < 10)) {
        setFormErrors({
          ...formErrors,
          description: "Description must be at least 10 characters long",
      })
      } else if ((name === "description" ) && (value.length > 200)) {
        setFormErrors({
          ...formErrors,
          description: "Characters limit exceeded",
      })
      return;
    } else {
        setFormErrors({
          ...formErrors,
          description: null,
      })
      }
      if (REGEX_VALUES.SENTENCE.test(value)) {
        setForm({...form, description: value})
      } 
}

    const submitForm = async (e) => {
        e.preventDefault();

        const errors = validate(form);
        if (errors) {
            for (var x in errors) {
                toast.error(errors[x]);
            }
            return;
        } else {
            const childPermissions = getChildPermissionsOnly();
            const params = {
                name: form.name,
                description: form.description,
                type: "custom",
                permissions: Array.from(childPermissions),
            };

            dispatch(settingsActions.editRole(params, roles?.id)).then(() => {
                setIsCreated(true);
            })
        }
    };

    const getChildPermissionsOnly = () => {
        const childPermissions = new Set();
        permissionsData?.data?.forEach(permission => {
            permission.companiesRolePermission.forEach(cp => {
                if (selectedPermissions.has(cp.id)) {
                    childPermissions.add(cp.id);
                }
            });
        });
        return childPermissions;
    };

    const editValues = {
        name: (roles?.name?.toLowerCase() === "hr admin" ? "HR" : roles?.name) || "",
        description: roles?.description || "",
    };

    useEffect(() => {
        setForm({ ...editValues });
    }, [roles]);
    
        
    const isButtonDisabled = useMemo(() => {

        if (activeIndex === 0 && (form.name.length < 2 || form.description.length < 10)){
          return true;
        } else if (activeIndex === 1 && (selectedPermissions.size < 1)){
          return true;
        }
      }, [form, activeIndex, selectedPermissions])
    


    const firstContent = (
        <div>
            <div>
                <div className='flex my-2'>
                    <div className='pr-3'>
                        <img src={greenbar} alt="" />
                    </div>
                    <div>
                        <img src={whitebar} alt="" />
                    </div>
                </div>
            </div>

            <div className='py-4 text-ep-gray-700'>
                <Input
                    type="text"
                    label="Role Name"
                    name="name"
                    required
                    value={form.name}
                    placeholder="E.g., Head of Engineering"
                    onChange={handleNameChange}
                    tagText={formErrors?.name}
                    tagTextIsRed
                />
                <Textarea
                    type="text"
                    label="Role"
                    name="description"
                    required
                    value={form.description}
                    className="form-input-textarea h-48 w-full"
                    placeholder="Enter a description..."
                    onChange={handleDescriptionChange}
                    tagText={formErrors?.description}
                    tagTextIsRed
                />
            </div>

            <div className="p-1 mt-4 flex justify-end space-x-3">
                <div className='flex'>
                    <div className='pr-5'>
                        <button onClick={() => setIsOpen(false)} type="button" className="btn btn-lg w-full rounded-lg whitespace-nowrap border border-ep-primary-600 text-ep-primary-600 bg-white">
                            Cancel
                        </button>
                    </div>
                    <div>
                        <button onClick={() => setActiveIndex(prev => prev + 1)} type="button" disabled={isButtonDisabled} className="btn btn-lg w-full rounded-lg whitespace-nowrap border border-ep-primary-600 text-white bg-ep-primary-600">
                            Next
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    const secondContent = (
        <form onSubmit={submitForm}>
            <div>
                <div>
                    <div className='flex my-3 font-medium text-ep-gray-600'>
                        <div className='pr-3'>
                            <img src={greenbar} alt="" />
                            <div className='py-3'>Role Details</div>
                        </div>
                        <div>
                            <img src={greenbar} alt="" />
                            <div className='py-3'>Permissions</div>
                        </div>
                    </div>
                </div>

                <div className='flex my-5'>
                    <div>
                        <SlideToggle
                            onChange={handleToggle}
                            active={toggleActive}
                        />
                    </div>
                    <div className='text-ep-gray-600 pl-3'>Assign all permissions for this role</div>
                </div>
                <div>
                    {permissionsData?.data?.map(permission => (
                        <div key={permission.id} className="role-section mb-6">
                            <div className="flex my-6 bg-ep-gray-100 py-6 px-2 rounded-lg">
                                <label className='inline-flex items-center'>
                                    <input
                                        type="checkbox"
                                        checked={selectedPermissions.has(permission.parentPermission.id)}
                                        onChange={() => handleCheckboxChange(
                                            permission.parentPermission.id,
                                            true,
                                            permission.companiesRolePermission.map(companyPermission => companyPermission.id)
                                        )}
                                        className={`form-checkbox w-5 h-5 multi-select-header mr-3 ${permission.disabled ? 'cursor-not-allowed opacity-50' : ''}`}
                                    />
                                    <div className="flex items-center">
                                        <h3 className='text-black font-bold'>{permission.parentPermission.name}</h3>
                                        <span className="mx-1">-</span>
                                        <p className='text-ep-gray-600 font-medium'>{' '}{permission.parentPermission.description}</p>
                                    </div>
                                </label>
                            </div>
                            <div className="">
                                {permission?.companiesRolePermission?.map(companyPermission => (
                                    <div key={companyPermission.id} className="permission-item mb-4">
                                        <label className='inline-flex items-center px-2'>
                                            <input
                                                type="checkbox"
                                                checked={selectedPermissions.has(companyPermission.id)}
                                                onChange={() => handleCheckboxChange(companyPermission.id)}
                                                className={`form-checkbox w-5 h-5 multi-select-header mr-3`}
                                            />
                                            <span className="font-normal text-ep-gray-300">
                                                {companyPermission.companyPermission.description}
                                            </span>
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
                <div className="p-1 mt-4 flex justify-end space-x-3">
                    <div className='flex'>
                        <div className='pr-5'>
                            <button onClick={() => setActiveIndex(prev => prev - 1)} type="button" className="btn btn-lg w-full rounded-lg whitespace-nowrap border border-ep-primary-600 text-ep-primary-600 bg-white">
                                Back
                            </button>
                        </div>
                        <div>
                            <ButtonLoader type="submit" loading={!!editRoleLoading} disabled={isButtonDisabled} className="btn btn-lg w-full rounded-lg whitespace-nowrap border border-ep-primary-600 text-white bg-ep-primary-600">
                                Edit Role
                            </ButtonLoader>
                        </div>
                    </div>
                </div>
            </div>
        </form>
    );

    const renderModalContent = (activeIndex) => {
        switch (activeIndex) {
            case 0:
                return firstContent;
            case 1:
                return secondContent;
            default:
                return null;
        }
    }

    useEffect(() => {
        if (editRole?.error === false && isCreated) {
            getAllRoles();
            setIsOpenModalAlertSuccess(true);
            setIsCreated(false);
            setIsOpen(false);
            setForm({ name: "", description: "", permissions: [] });
            setActiveIndex(0);
        }
    }, [editRole?.error, isCreated]);

    return (
        <>
            <ModalAlert
                successText={editRole?.message}
                dismissText="Close"
                isOpenModalAlert={isOpenModalAlertSuccess}
                setIsOpenModalAlert={setIsOpenModalAlertSuccess}
            />
            <FormDrawer display="center" size="xl" isOpen={isOpen} setIsOpen={setIsOpen}>
                <div className="p-6">
                    <FormDrawer.Title
                        title="Edit Role"
                        showCloseButton={true}
                        close={setIsOpen}
                    />
                    {renderModalContent(activeIndex)}
                </div>
            </FormDrawer>
        </>
    );
}

export default EditRoleModal;
